import { CalendarDays, Clock, ChevronLeft, ChevronRight, Shield, User, Users } from 'lucide-react'
import { forwardRef, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { statusTone, type Person } from '../../pages/time/timeData'
import type { Scope } from '../../pages/time/ui'
import { dialogNameAttrs } from './dialogName'

/** The banner every page shows when the signed-in user has no personnel record: nothing here can work without one, and the fix is an HR action. */
export function NoEmployeeRecord() {
  const { t } = useTranslation()
  return (
    <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      {t('time.noEmployeeRecord')}
    </p>
  )
}

const SCOPE_ICON: Record<Scope, typeof User> = {
  own: User,
  team: Users,
  all: Shield,
  awaiting: Clock,
  admin: Shield,
}

export function ScopeTabs({
  scope,
  scopes,
  onChange,
  counts,
  fill = false,
}: {
  scope: Scope
  scopes: Scope[]
  onChange: (scope: Scope) => void
  counts?: Partial<Record<Scope, number>>
  fill?: boolean
}) {
  const { t } = useTranslation()
  if (scopes.length < 2) return null
  return (
    <div
      role="group"
      aria-label={t('time.scope')}
      className={`flex max-w-full flex-wrap gap-0.5 rounded-lg border border-border p-0.5 ${
        fill ? 'w-full' : 'shrink-0'
      }`}
    >
      {scopes.map((key) => {
        const Icon = SCOPE_ICON[key]
        const active = scope === key
        const count = counts?.[key] ?? 0
        const full = t(`time.scope_${key}`)
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={active}
            title={full}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium sm:px-3 ${
              fill ? 'flex-1 justify-center' : 'shrink-0'
            } ${
              active
                ? 'bg-accent text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
                : 'text-text-muted hover:bg-slate-100 dark:hover:bg-slate-700/60'
            }`}
          >
            <Icon className={`hidden h-3.5 w-3.5 shrink-0 ${fill ? '' : 'sm:block'}`} />
            <span className="sm:hidden">{t(`time.scope_${key}_short`)}</span>
            <span className="hidden sm:inline">{full}</span>
            {count > 0 && (
              <span
                className={`rounded-full px-1.5 text-[10px] tabular-nums ${
                  active ? 'bg-white/25' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation()
  if (!status) return <span className="text-slate-500 dark:text-slate-400">—</span>
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${statusTone(status)}`}>
      {t(`time.status.${status}`, status)}
    </span>
  )
}

export function PeriodNav({
  label,
  sub,
  onPrev,
  onNext,
  onToday,
}: {
  label: string
  sub?: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-1">
      <button
        type="button"
        onClick={onPrev}
        aria-label={t('time.prev')}
        className="rounded-lg border border-border p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/60"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1 px-2 text-center sm:min-w-[13rem] sm:flex-none">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</div>
        {sub && <div className={mutedTextClass}>{sub}</div>}
      </div>
      <button
        type="button"
        onClick={onNext}
        aria-label={t('time.next')}
        className="rounded-lg border border-border p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/60"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onToday}
        className="ml-1 flex items-center gap-1 rounded-lg border border-border px-2 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/60"
      >
        <CalendarDays className="h-3.5 w-3.5" />
        {t('time.today')}
      </button>
    </div>
  )
}

export function PersonList({
  people,
  selectedId,
  onSelect,
  meta,
  empty,
  fill,
}: {
  people: Person[]
  selectedId: string | null
  onSelect: (id: string) => void
  meta?: (person: Person) => React.ReactNode
  empty: string
  /** Fill the parent's own height instead of `.list-viewport`'s fixed `calc(100dvh - 21rem)` guess. */
  fill?: boolean
}) {
  if (people.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-text-muted">
        {empty}
      </p>
    )
  }
  return (
    <ul
      className={`${fill ? 'h-full min-h-0 overflow-auto' : 'list-viewport'} divide-y divide-slate-100 rounded-xl border border-border-subtle bg-surface dark:divide-slate-700/60`}
    >
      {people.map((person) => (
        <li key={person.id}>
          <button
            type="button"
            onClick={() => onSelect(person.id)}
            aria-current={selectedId === person.id ? 'true' : undefined}
            className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm ${
              selectedId === person.id
                ? 'bg-blue-50 dark:bg-blue-950/40'
                : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'
            }`}
          >
            <span className="min-w-0">
              <span className="block truncate font-medium text-slate-800 dark:text-slate-100">
                {person.name}
              </span>
              <span
                className={`block truncate text-xs ${
                  selectedId === person.id ? 'text-slate-600 dark:text-slate-300' : 'text-text-muted'
                }`}
              >
                {person.position || person.team}
              </span>
            </span>
            {meta?.(person)}
          </button>
        </li>
      ))}
    </ul>
  )
}

export interface ResponsiveTableColumn<T> {
  key: string
  header: React.ReactNode
  cell: (row: T) => React.ReactNode
  align?: 'right'
  hideOnCard?: boolean
}

export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  cardFooter,
  emptyState,
}: {
  columns: ResponsiveTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  cardFooter?: (row: T) => React.ReactNode
  emptyState?: React.ReactNode
}) {
  if (rows.length === 0 && emptyState == null) return null
  const cardColumns = columns.filter((c) => !c.hideOnCard)
  const isEmpty = rows.length === 0

  return (
    <>
      {isEmpty ? (
        <div className="sm:hidden">{emptyState}</div>
      ) : (
        <ul className="space-y-2 sm:hidden">
          {rows.map((row) => (
            <li key={rowKey(row)}>
              <div
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') onRowClick(row)
                      }
                    : undefined
                }
                className={`space-y-1.5 rounded-xl border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-800 ${
                  onRowClick ? 'cursor-pointer' : ''
                }`}
              >
                {cardColumns.map((c) => (
                  <div key={c.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="text-xs text-slate-500 dark:text-slate-400">{c.header}</span>
                    {/* A value too wide for the rest of the line wraps whole to its own line instead of squeezing. */}
                    <span className={`ml-auto max-w-full min-w-0 ${c.align === 'right' ? 'text-right' : ''}`}>{c.cell(row)}</span>
                  </div>
                ))}
                {cardFooter && <div className="pt-1">{cardFooter(row)}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 sm:block">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`p-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={columns.length} className="p-3">
                  {emptyState}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-slate-100 text-slate-600 last:border-0 dark:border-slate-700/60 dark:text-slate-300 ${
                    onRowClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40' : ''
                  }`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`p-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

export const btnPrimary =
  'rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
export const btnGhost =
  'rounded-lg border border-border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-40 dark:hover:bg-slate-700/60 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
export const btnApprove =
  'rounded-lg bg-approve px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-40 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
export const btnReject =
  'rounded-lg border border-red-300 px-3 py-2 text-sm text-reject hover:bg-red-50 disabled:opacity-40 dark:border-red-900 dark:hover:bg-red-950/40 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
export const inputClass =
  'rounded-lg border-border bg-surface text-sm dark:text-slate-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-600 dark:disabled:bg-slate-900 dark:disabled:text-slate-400'
export const cardClass = 'rounded-xl border border-border-subtle bg-surface p-4'
export const labelClass = 'mb-1 block text-slate-600 dark:text-slate-300'
export const mutedTextClass = 'text-xs text-text-muted'
export const emptyStateClass = 'p-8 text-center text-text-muted'

export function Req() {
  return (
    <span className="text-red-500" aria-hidden="true">
      {' '}
      *
    </span>
  )
}
const modalBackdropClass =
  'modal-backdrop fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-slate-900/40 sm:items-start sm:p-4 sm:py-10'
export const dialogPanelClass =
  'w-full min-h-dvh space-y-4 bg-white p-5 max-sm:pt-[max(1.25rem,env(safe-area-inset-top))] max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-xl dark:bg-slate-800 sm:min-h-0 sm:max-w-lg sm:rounded-xl'

/** A panel that keeps its own width and padding still clears the notch; the same at inset 0. */
export const dialogInsetPad = {
  2: 'max-sm:pt-[max(0.5rem,env(safe-area-inset-top))] max-sm:pb-[max(0.5rem,env(safe-area-inset-bottom))]',
  4: 'max-sm:pt-[max(1rem,env(safe-area-inset-top))] max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]',
  5: 'max-sm:pt-[max(1.25rem,env(safe-area-inset-top))] max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))]',
  6: 'max-sm:pt-[max(1.5rem,env(safe-area-inset-top))] max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]',
} as const

const wideDialogMaxWidth = {
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '5xl': 'sm:max-w-5xl',
} as const
export function dialogPanelWideClass(size: keyof typeof wideDialogMaxWidth): string {
  return dialogPanelClass.replace('sm:max-w-lg', wideDialogMaxWidth[size])
}

export function dialogScrollPanelClass(size: keyof typeof wideDialogMaxWidth | 'lg' = 'lg'): string {
  const max = size === 'lg' ? 'sm:max-w-lg' : wideDialogMaxWidth[size]
  return `flex max-h-dvh w-full flex-col bg-white max-sm:pt-[env(safe-area-inset-top)] max-sm:pb-[env(safe-area-inset-bottom)] shadow-xl dark:bg-slate-800 sm:max-h-[85dvh] sm:rounded-xl ${max}`
}
export const dialogScrollBodyClass = 'min-h-0 flex-1 space-y-4 overflow-y-auto p-5'
export const dialogScrollFooterClass =
  'flex shrink-0 justify-end border-t border-slate-200 px-5 py-3 dark:border-slate-700'

export const ModalBackdrop = forwardRef<
  HTMLDivElement,
  {
    onClose: () => void
    dismissOnBackdrop?: boolean
    role?: string
    'aria-label'?: string
    'aria-labelledby'?: string
    children: ReactNode
  }
>(function ModalBackdrop(
  {
    onClose,
    dismissOnBackdrop = true,
    role = 'dialog',
    'aria-label': ariaLabel,
    'aria-labelledby': labelledBy,
    children,
  },
  ref,
) {
  const downOnBackdrop = useRef(false)

  return (
    <div
      ref={ref}
      className={modalBackdropClass}
      role={role}
      aria-modal="true"
      {...dialogNameAttrs(labelledBy, ariaLabel)}
      onMouseDown={(event: MouseEvent<HTMLDivElement>) => {
        downOnBackdrop.current = dismissOnBackdrop && event.target === event.currentTarget
      }}
      onMouseUp={(event: MouseEvent<HTMLDivElement>) => {
        if (downOnBackdrop.current && event.target === event.currentTarget) onClose()
        downOnBackdrop.current = false
      }}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') onClose()
      }}
    >
      {children}
    </div>
  )
})

export * from './Badge'
export * from './Button'
export { MultiSelect } from './MultiSelect'
export * from './Field'
export * from './Notice'
export * from './FillBox'
export { HelpTip } from './HelpTip'
export * from './States'
export { Switch } from './Switch'
export * from './Toolbar'
export { width } from './kit'
